import { entity as E } from "./schema.js";
export const manufacturing = {
"BB-MFG-01": [
E("mfg_plan", "Production plan", "A versioned production plan for a site and planning horizon.", `site_id|>inv_site.site_id|Site producing the planned output
plan_code|CODE(40)|Site-issued production plan reference
revision|INTEGER|Plan revision number
starts_on|DATE|Inclusive planning horizon start
ends_on|DATE|Inclusive planning horizon end
status|CODE(20)|Draft, approved or superseded plan`, ["site_id,plan_code,revision"], "Only one approved revision per plan and horizon; end cannot precede start."),
E("mfg_demand", "Production demand", "An item requirement recorded within a planning revision.", `plan_id|>mfg_plan.id|Owning production plan
line_no|INTEGER|Demand sequence within the plan
item_id|>inv_item.item_id|Required output item
required_qty|QTY|Demand quantity in the stated unit
uom|CODE(16)|Unit of measure
due_on|DATE|Required completion date`, ["plan_id,line_no"], "Required quantity must be positive; demand is not proof of capacity or material availability."),
E("mfg_work_order", "Production work order", "A scheduled quantity of an item released against planned demand.", `demand_id|>mfg_demand.id|Originating demand
order_code|CODE(40)|Production order reference
planned_start|INSTANT|Scheduled start time
planned_finish|INSTANT|Scheduled finish time
planned_qty|QTY|Output quantity in demand unit
status|CODE(20)|Planned, released, started or closed`, ["order_code"], "Order codes assumed unique in the manufacturing business; split orders may cover one demand."),
E("mfg_plan_revision_review", "Plan approval", "A review decision on a specific plan revision.", `plan_id|>mfg_plan.id|Reviewed plan revision
review_no|INTEGER|Review sequence
reviewer_id|>org_employee.employee_id|Employee making the decision
decided_at|INSTANT|Decision timestamp
decision|CODE(20)|Approved, rejected or revise
reason|TEXT(1000)|Decision justification`, ["plan_id,review_no"], "Approval authority and replacement of earlier decisions require workflow validation.")
],
"BB-MFG-02": [
E("mfg_operation", "Work-order operation", "One executable step of a production work order.", `work_order_id|>mfg_work_order.id|Production order
sequence_no|INTEGER|Operation position within order
operation_name|TEXT(120)|Work instruction title
planned_minutes|QTY|Planned processing duration in minutes
status|CODE(20)|Ready, running, paused or finished`, ["work_order_id,sequence_no"], "Precedence and parallel-operation rules must be checked before dispatch."),
E("mfg_operator_assignment", "Operator assignment", "An employee assigned to perform a work-order operation.", `operation_id|>mfg_operation.id|Assigned operation
employee_id|>org_employee.employee_id|Assigned operator
starts_at|INSTANT|Assignment start
ends_at|INSTANT?|Assignment end
role_code|CODE(30)|Operator responsibility`, ["operation_id,employee_id,starts_at"], "Qualification and shift availability require separate verification."),
E("mfg_progress", "Operation progress", "A recorded occurrence of progress or stoppage on an operation.", `operation_id|>mfg_operation.id|Reported operation
event_no|INTEGER|Sequence within operation
occurred_at|INSTANT|Actual event time
event_kind|CODE(20)|Start, stop, resume or complete
reason|TEXT(300)?|Explanation of stoppage or exception`, ["operation_id,event_no"], "Event sequence is not deduplicated by timestamp."),
E("mfg_actual_quantity", "Production quantity", "Measured consumption, good output or scrap from an operation.", `operation_id|>mfg_operation.id|Operation producing the record
record_no|INTEGER|Quantity record sequence
item_id|>inv_item.item_id|Consumed or produced item
lot_id|>inv_lot.lot_id?|Related inventory lot
quantity_kind|CODE(20)|Consumed, good_output or scrap
quantity|QTY|Positive magnitude
uom|CODE(16)|Quantity unit`, ["operation_id,record_no"], "Material balance must account for conversion, rework and scrap without counting output twice.")
],
"BB-MFG-03": [
E("mfg_machine", "Machine capacity profile", "Manufacturing capacity characteristics attached to an existing asset.", `asset_id|>ast_asset.asset_id|Physical machine
machine_code|CODE(40)|Scheduling identifier
capacity_uom|CODE(16)|Capacity measure such as machine_minutes
nominal_capacity|QTY|Capacity for one standard shift
setup_group|CODE(40)?|Compatible setup family`, ["asset_id", "machine_code"], "Capacity is a planning assumption and is adjusted by calendar availability."),
E("mfg_capacity_slot", "Machine calendar slot", "A dated interval of available or unavailable machine capacity.", `machine_id|>mfg_machine.id|Machine
starts_at|INSTANT|Slot start
ends_at|INSTANT|Slot end
available_capacity|QTY|Usable capacity in machine capacity unit
availability|CODE(20)|Available, maintenance or shutdown`, ["machine_id,starts_at"], "Calendar slots must not overlap for a machine."),
E("mfg_machine_load", "Machine load assignment", "Operation load allocated to an explicit machine calendar interval.", `slot_id|>mfg_capacity_slot.id|Capacity interval
operation_id|>mfg_operation.id|Scheduled operation
load_no|INTEGER|Split load number
reserved_capacity|QTY|Capacity consumption including setup
priority|INTEGER|Dispatch priority`, ["slot_id,operation_id,load_no"], "Total load must not exceed available capacity without an approved overload."),
E("mfg_machine_constraint", "Machine scheduling constraint", "A dated operating constraint applicable to a capacity profile.", `machine_id|>mfg_machine.id|Constrained machine
constraint_code|CODE(40)|Constraint identifier
valid_from|DATE|Effective date
constraint_kind|CODE(30)|Tooling, batch_size or setup_restriction
constraint_text|TEXT(1000)|Reviewed scheduling rule`, ["machine_id,constraint_code,valid_from"], "Constraint text is descriptive; no scheduling solver is included.")
],
"BB-MFG-04": [
E("mfg_indent", "Material indent", "A numbered request for materials by an organizational unit.", `unit_id|>org_unit.unit_id|Requesting unit
indent_number|CODE(40)|Unit-issued indent number
requested_on|DATE|Request date
requester_id|>org_employee.employee_id|Requesting employee
status|CODE(20)|Draft, submitted, approved or closed`, ["unit_id,indent_number"], "Approval does not reserve or issue stock."),
E("mfg_indent_line", "Indent line", "Requested item quantity and required date.", `indent_id|>mfg_indent.id|Parent indent
line_no|INTEGER|Line number
item_id|>inv_item.item_id|Requested item
quantity|QTY|Requested quantity
uom|CODE(16)|Quantity unit
required_on|DATE|Required delivery date`, ["indent_id,line_no"], "Quantity must be positive; substitutions require reviewed item mapping."),
E("mfg_indent_approval", "Indent approval", "Dated decision on an indent.", `indent_id|>mfg_indent.id|Reviewed indent
step_no|INTEGER|Approval sequence
approver_id|>org_employee.employee_id|Decision maker
decision|CODE(20)|Approve, reject or return
decided_at|INSTANT|Decision time`, ["indent_id,step_no"], "Approval sequence and delegated authority are application rules."),
E("mfg_indent_issue", "Indent fulfillment", "A partial fulfillment of an indent by a recorded stock movement.", `line_id|>mfg_indent_line.id|Requested line
movement_id|>inv_movement.movement_id|Stock issue movement
quantity|QTY|Quantity satisfied in indent unit
fulfilled_at|INSTANT|Fulfillment time`, ["line_id,movement_id"], "Total issued and purchased quantities must be reconciled against request.")
],
"BB-MFG-05": [
E("mfg_purchase_order", "Manufacturing purchase order", "A supplier order for required manufacturing materials.", `supplier_id|>sup_account.supplier_id|Supplier account
order_number|CODE(40)|Purchasing order reference
ordered_on|DATE|Order date
currency|CODE(3)|Order currency
status|CODE(20)|Draft, issued or closed`, ["order_number"], "Order reference assumed unique within the purchasing business."),
E("mfg_purchase_line", "Purchase line", "An ordered material with optional source indent.", `purchase_order_id|>mfg_purchase_order.id|Purchase order
line_no|INTEGER|Order line number
indent_line_id|>mfg_indent_line.id?|Originating indent line
item_id|>inv_item.item_id|Ordered item
quantity|QTY|Ordered quantity
uom|CODE(16)|Order unit
unit_price|MONEY|Price in order currency per order unit`, ["purchase_order_id,line_no"], "Many purchase lines may satisfy one indent; quantities and currency must reconcile."),
E("mfg_delivery_commitment", "Supplier commitment", "A dated partial delivery promised for a purchase line.", `line_id|>mfg_purchase_line.id|Purchase line
schedule_no|INTEGER|Delivery schedule number
promised_on|DATE|Committed arrival date
quantity|QTY|Promised quantity in line unit`, ["line_id,schedule_no"], "Delivery commitments may be revised; changes require history at runtime."),
E("mfg_receipt", "Manufacturing receipt", "A receipt of an ordered material lot awaiting inspection or release.", `line_id|>mfg_purchase_line.id|Purchase line
receipt_number|CODE(40)|Receiving reference
lot_id|>inv_lot.lot_id|Received lot
received_at|INSTANT|Receipt time
quantity|QTY|Received quantity in purchase unit
status|CODE(20)|Awaiting_inspection, accepted or held`, ["receipt_number"], "Receipt records do not imply quality acceptance or invoice payment.")
],
"BB-MFG-06": [
E("mfg_incoming_inspection", "Incoming inspection", "Inspection request for a received material lot.", `receipt_id|>mfg_receipt.id|Inspected receipt
inspection_number|CODE(40)|Inspection reference
specification_ref|TEXT(120)|Applicable reviewed specification revision
requested_at|INSTANT|Request time
sample_quantity|QTY|Planned sample quantity`, ["inspection_number"], "Sampling plans and acceptance thresholds require domain approval."),
E("mfg_incoming_result", "Incoming test result", "A measured characteristic in a receipt inspection.", `inspection_id|>mfg_incoming_inspection.id|Inspection request
sample_no|INTEGER|Sample number
characteristic|CODE(60)|Measured characteristic
numeric_value|QTY?|Measured value
uom|CODE(16)?|Measurement unit
outcome|CODE(20)|Pass, fail or inconclusive`, ["inspection_id,sample_no,characteristic"], "Outcome is an assessed result, not computed automatically from the numeric value."),
E("mfg_receipt_disposition", "Receipt disposition", "A quality decision assigning received quantity to use, hold or rejection.", `inspection_id|>mfg_incoming_inspection.id|Supporting inspection
decision_no|INTEGER|Decision sequence
decision|CODE(20)|Accept, reject, hold or concession
quantity|QTY|Affected quantity
reviewer_id|>org_employee.employee_id|Authorizing employee
decided_at|INSTANT|Decision timestamp`, ["inspection_id,decision_no"], "Disposition quantities must not exceed received quantity; stock status changes are separate operations.")
],
"BB-MFG-07": [
E("mfg_quality_check", "Production quality check", "A quality check for an operation or finished output lot.", `operation_id|>mfg_operation.id|Producing operation
lot_id|>inv_lot.lot_id?|Finished lot
check_number|CODE(40)|Quality-check reference
stage|CODE(20)|In_process or finished_goods
specification_ref|TEXT(120)|Applicable quality specification`, ["check_number"], "Finished-goods checks require an output lot before release."),
E("mfg_quality_result", "Production check result", "A characteristic and measured outcome for a production check.", `check_id|>mfg_quality_check.id|Quality check
sample_no|INTEGER|Sample number
characteristic|CODE(60)|Characteristic identifier
value_text|TEXT(200)|Measured numeric or descriptive result
uom|CODE(16)?|Measurement unit
outcome|CODE(20)|Pass, fail or inconclusive`, ["check_id,sample_no,characteristic"], "Test-specific validation determines accepted value formats."),
E("mfg_defect", "Production defect", "A reported defect linked to a failed or suspect result.", `result_id|>mfg_quality_result.id|Supporting result
defect_number|CODE(40)|Defect reference
severity|CODE(20)|Reviewed defect severity
description|TEXT(1000)|Observed defect
status|CODE(20)|Open, contained or closed`, ["defect_number"], "Defects remain traceable even if later disposition accepts the lot."),
E("mfg_corrective_action", "Corrective action", "An assigned action and verification for a production defect.", `defect_id|>mfg_defect.id|Addressed defect
action_no|INTEGER|Action sequence
owner_id|>org_employee.employee_id|Responsible employee
action_text|TEXT(1000)|Corrective action
due_on|DATE|Target completion
verified_at|INSTANT?|Verification time`, ["defect_id,action_no"], "Closing a defect requires a reviewed verification outcome."),
E("mfg_quality_release", "Output release decision", "Recorded authorization to release or hold checked output.", `check_id|>mfg_quality_check.id|Check being assessed
decision_no|INTEGER|Decision sequence
reviewer_id|>org_employee.employee_id|Authorizing employee
decision|CODE(20)|Release, hold or reject
decided_at|INSTANT|Authorization time`, ["check_id,decision_no"], "A release is a decision record, not an automatic stock movement.")
],
"BB-MFG-08": [
E("mfg_customer_commitment", "Customer delivery promise", "A manufacturing promise against an existing sales-order line.", `order_line_id|>sal_order_line.order_line_id|Commercial order line
promise_no|INTEGER|Promise sequence
promised_on|DATE|Promised delivery
quantity|QTY|Promised quantity in sales line unit
priority|INTEGER|Customer service priority`, ["order_line_id,promise_no"], "Customer orders and parties are reused through the Sales dependency."),
E("mfg_demand_allocation", "Customer production allocation", "Quantity assigned from a work order to a customer promise.", `commitment_id|>mfg_customer_commitment.id|Customer delivery promise
work_order_id|>mfg_work_order.id|Supplying work order
quantity|QTY|Allocated quantity
allocated_at|INSTANT|Allocation timestamp`, ["commitment_id,work_order_id"], "Allocated supply cannot exceed available production output."),
E("mfg_fulfillment_event", "Customer fulfillment event", "Partial shipment progress against a manufacturing promise.", `commitment_id|>mfg_customer_commitment.id|Delivery promise
shipment_line_id|>sal_shipment_line.shipment_line_id|Shipment line
quantity|QTY|Quantity fulfilled
recorded_at|INSTANT|Progress timestamp`, ["commitment_id,shipment_line_id"], "Fulfillment must reconcile to the shared sales shipment ledger.")
],
"BB-MFG-09": [
E("mfg_stock_policy", "Production stock policy", "Item-location stock targets for production supply.", `item_id|>inv_item.item_id|Stock item
location_id|>inv_location.stock_location_id|Storage location
effective_from|DATE|Policy start
minimum_qty|QTY|Minimum desired balance
reorder_qty|QTY|Proposed replenishment quantity
uom|CODE(16)|Stock unit`, ["item_id,location_id,effective_from"], "Targets guide replenishment; actual balances derive from shared movements."),
E("mfg_stock_count", "Manufacturing cycle count", "A count session at an inventory location.", `location_id|>inv_location.stock_location_id|Counted location
count_number|CODE(40)|Count session reference
counted_at|INSTANT|Snapshot time
status|CODE(20)|Open, reconciled or posted`, ["count_number"], "Freeze or reconcile movements occurring across the count timestamp."),
E("mfg_count_line", "Manufacturing count line", "A count and assessed variance for an item or lot.", `count_id|>mfg_stock_count.id|Count session
line_no|INTEGER|Line number
item_id|>inv_item.item_id|Counted item
lot_id|>inv_lot.lot_id?|Counted lot
counted_qty|QTY|Observed quantity
uom|CODE(16)|Count unit
adjustment_id|>inv_movement.movement_id?|Approved adjusting movement`, ["count_id,line_no"], "Variance postings require approval and must not overwrite movement history.")
],
"BB-MFG-10": [
E("mfg_bom", "Bill of materials revision", "An effective-dated product structure and expected yield.", `item_id|>inv_item.item_id|Manufactured output item
revision|CODE(20)|BOM revision
valid_from|DATE|Start of effectivity
valid_to|DATE?|End of effectivity
yield_quantity|QTY|Expected output quantity
yield_uom|CODE(16)|Output unit`, ["item_id,revision"], "Only reviewed revisions can be released; recursive component cycles are invalid."),
E("mfg_bom_component", "BOM component", "One material input to a bill-of-materials revision.", `bom_id|>mfg_bom.id|BOM revision
line_no|INTEGER|Component position
item_id|>inv_item.item_id|Required material
quantity|QTY|Quantity per BOM yield
uom|CODE(16)|Component unit
scrap_percent|PCT|Planned loss allowance`, ["bom_id,line_no"], "Loss allowances and substitutions require engineering approval."),
E("mfg_routing_step", "Production routing step", "An ordered manufacturing instruction for a product revision.", `bom_id|>mfg_bom.id|Product structure revision
sequence_no|INTEGER|Step sequence
step_name|TEXT(120)|Work instruction
setup_minutes|QTY|Expected setup duration
run_minutes|QTY|Run duration per yield`, ["bom_id,sequence_no"], "A sequence establishes default order; complex precedence requires additional constraints."),
E("mfg_resource_requirement", "Routing resource requirement", "Capacity or tooling needed by a routing step.", `step_id|>mfg_routing_step.id|Routing step
requirement_no|INTEGER|Requirement sequence
resource_class|CODE(40)|Machine, labor or tooling category
quantity|QTY|Required amount
uom|CODE(16)|Resource unit`, ["step_id,requirement_no"], "Resource classes must be mapped to available qualified assets or skills.")
],
"BB-MFG-11": [
E("mfg_failure", "Equipment failure", "A manufacturing interruption tied to an existing asset.", `asset_id|>ast_asset.asset_id|Failed equipment
failure_number|CODE(40)|Failure record reference
started_at|INSTANT|Failure start
restored_at|INSTANT?|Return to service
failure_mode|CODE(60)|Failure classification`, ["failure_number"], "Downtime is measured from actual event times with overlap reconciliation."),
E("mfg_repair_job", "Manufacturing repair", "A maintenance work order addressing an equipment failure.", `failure_id|>mfg_failure.id|Reported failure
work_order_id|>ast_work_order.work_order_id|Shared maintenance work order
repair_description|TEXT(1000)|Planned repair
status|CODE(20)|Planned, executing or verified`, ["failure_id,work_order_id"], "Maintenance plans, assets and task histories stay in their shared package."),
E("mfg_repair_consumption", "Repair spare usage", "Spare parts and labor consumed by a repair.", `repair_id|>mfg_repair_job.id|Repair job
line_no|INTEGER|Consumption sequence
item_id|>inv_item.item_id?|Consumed spare item
employee_id|>org_employee.employee_id?|Employee for labor consumption
quantity|QTY|Consumed amount
uom|CODE(16)|Parts unit or labor hours`, ["repair_id,line_no"], "Exactly one of spare item or employee is required; stock issues need matching movements.")
],
"BB-MFG-12": [
E("mfg_cost_sheet", "Production cost sheet", "A versioned cost assessment for a work order.", `work_order_id|>mfg_work_order.id|Costed production order
revision|INTEGER|Assessment revision
currency|CODE(3)|Cost currency
cost_basis|CODE(20)|Standard, estimated or actual
prepared_on|DATE|Assessment date`, ["work_order_id,revision"], "This models costing evidence, not accounting policy or automatic posting."),
E("mfg_cost_line", "Production cost component", "A material, labor or overhead amount in a cost sheet.", `sheet_id|>mfg_cost_sheet.id|Cost sheet
line_no|INTEGER|Component sequence
account_id|>fin_account.account_id|Applicable cost account
cost_kind|CODE(20)|Material, labor, overhead or variance
quantity|QTY|Cost driver quantity
cost_driver_uom|CODE(16)|Cost driver unit such as kilograms, labor_hours or machine_hours
rate|MONEY|Rate in sheet currency per cost driver unit
amount|MONEY|Assessed amount`, ["sheet_id,line_no"], "Rate basis, rounding and overhead allocation rules require local policy."),
E("mfg_cost_posting", "Production posting reference", "A link from cost evidence to an approved shared ledger journal.", `sheet_id|>mfg_cost_sheet.id|Posted cost sheet
journal_id|>fin_journal.journal_id|Ledger journal
posted_amount|MONEY|Amount in sheet currency
posted_at|INSTANT|Posting timestamp`, ["sheet_id,journal_id"], "Journal balancing and posting controls belong to the ledger implementation.")
]
};
